import { useState, useEffect, useMemo, useRef } from 'react';
import { Check, ChevronsUpDown, Building2, Search } from 'lucide-react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { Organization } from './types';
import {
    IconContainer,
    DropdownIconContainer,
    OrganizationInfo,
    OrganizationName,
    OrganizationLabel,
    DropdownOrganizationName,
    DropdownOrganizationLabel,
} from './styled';
import useInitialData from '../../../contexts/InitialDataContext/useInitialData';
import type { Agent } from '../../../contexts/InitialDataContext/types';

export function OrganizationSwitcher() {
    const { data, isLoadingInitialData, error } = useInitialData();
    const navigate = useNavigate();
    const { agentId } = useParams();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Convert superagents to organizations format
    const organizations: Organization[] = useMemo(() => {
        if (!data?.superagents) return [];
        return data.superagents.map((agent: Agent) => ({
            id: agent.id,
            name: agent.name,
            icon: Building2,
        }));
    }, [data?.superagents]);

    // Filter organizations based on search query (no limit, allow scrolling through all)
    const filteredOrganizations = useMemo(() => {
        return organizations.filter(org =>
            org.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [organizations, searchQuery]);

    // Find the selected organization based on the current agentId from URL
    const selectedOrg = useMemo(() => {
        if (!agentId || organizations.length === 0) return null;
        return organizations.find(org => org.id === agentId) || null;
    }, [agentId, organizations]);

    // Handle organization selection
    const handleSelectOrganization = (org: Organization) => {
        setOpen(false);
        setSearchQuery('');

        // Extract the first path segment after /agents/{agentId}/
        const pathSegments = location.pathname.split('/').filter(Boolean);
        const topLevelSection = pathSegments[2] || 'workflows'; // pathSegments[0] = 'agents', [1] = agentId, [2] = section

        // Define valid top-level sections
        const validSections = [
            'apps',
            'workflows',
            'templates',
            'tools',
            'prompts',
            'secrets',
            'connections',
            'metrics',
            'system-agents',
        ];

        // Use the top-level section if valid, otherwise default to workflows
        const targetSection = validSections.includes(topLevelSection)
            ? topLevelSection
            : 'workflows';

        // Navigate to the new agent with just the top-level section
        navigate(`/agents/${org.id}/${targetSection}`, { replace: true });
    };

    // Maintain focus on search input when dropdown is open
    useEffect(() => {
        if (open && searchInputRef.current) {
            const timeoutId = setTimeout(() => {
                searchInputRef.current?.focus();
            }, 0);
            return () => clearTimeout(timeoutId);
        }
    }, [open, filteredOrganizations]);

    // Show skeleton loading while initial data is loading
    if (isLoadingInitialData) {
        return (
            <div className="h-auto p-3">
                <div className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-md" />
                    <div className="flex flex-col gap-2 flex-1">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-3 w-16" />
                    </div>
                </div>
            </div>
        );
    }

    // Show error state
    if (error) {
        return (
            <div className="flex items-center justify-center h-auto p-3">
                <span className="text-sm text-red-500">Failed to load organizations</span>
            </div>
        );
    }

    // Show empty state if no organizations
    if (organizations.length === 0) {
        return (
            <div className="flex items-center justify-center h-auto p-3">
                <span className="text-sm text-muted-foreground">No organizations available</span>
            </div>
        );
    }

    return (
        <DropdownMenu
            open={open}
            modal={false}
            onOpenChange={newOpen => {
                setOpen(newOpen);
                if (newOpen) {
                    // Focus search input when dropdown opens
                    setTimeout(() => {
                        searchInputRef.current?.focus();
                    }, 100);
                } else {
                    setSearchQuery('');
                }
            }}
        >
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    role="combobox"
                    aria-expanded={open}
                    className="w-full justify-between h-auto p-3 rounded-xl bg-gradient-to-br from-sky-400/25 via-blue-500/15 to-indigo-600/25 backdrop-blur-xl border border-sky-300/30 shadow-lg shadow-blue-500/10 ring-1 ring-inset ring-white/10 transition-all duration-300 hover:from-sky-400/35 hover:via-blue-500/25 hover:to-indigo-600/35 hover:shadow-blue-500/20 hover:border-sky-300/40"
                >
                    <div className="flex items-center gap-3 min-w-0 flex-1 overflow-hidden">
                        <IconContainer>
                            {selectedOrg?.icon ? <selectedOrg.icon /> : <Building2 />}
                        </IconContainer>
                        <OrganizationInfo>
                            <OrganizationName>
                                {selectedOrg?.name || 'Select Organization'}
                            </OrganizationName>
                            <OrganizationLabel>organization</OrganizationLabel>
                        </OrganizationInfo>
                    </div>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[300px] p-0 z-[1002]" side="right" align="start">
                <div className="p-3 border-b border-border">
                    <div
                        className="relative"
                        onKeyDown={e => {
                            // Prevent dropdown from closing when using arrow keys in search
                            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                                e.preventDefault();
                            }
                        }}
                        onClick={e => {
                            // Prevent dropdown from closing when clicking on search area
                            e.stopPropagation();
                        }}
                    >
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            ref={searchInputRef}
                            placeholder="Search organizations..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            onKeyDown={e => {
                                // Prevent dropdown from closing and handle navigation
                                if (e.key === 'Escape') {
                                    setOpen(false);
                                } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                                    e.preventDefault();
                                    // Keep focus on input while allowing keyboard navigation
                                    e.stopPropagation();
                                }
                            }}
                            onClick={e => {
                                // Prevent dropdown from closing when clicking input
                                e.stopPropagation();
                            }}
                            className="pl-9"
                        />
                    </div>
                </div>
                <div className="max-h-[420px] overflow-y-auto">
                    {filteredOrganizations.length > 0 ? (
                        filteredOrganizations.map(org => (
                            <DropdownMenuItem
                                key={org.id}
                                className="flex items-center gap-3 p-3 cursor-pointer"
                                onSelect={() => handleSelectOrganization(org)}
                            >
                                <DropdownIconContainer>
                                    <org.icon />
                                </DropdownIconContainer>
                                <OrganizationInfo>
                                    <DropdownOrganizationName>{org.name}</DropdownOrganizationName>
                                    <DropdownOrganizationLabel>
                                        organization
                                    </DropdownOrganizationLabel>
                                </OrganizationInfo>
                                <Check
                                    className={cn(
                                        'h-4 w-4',
                                        selectedOrg?.id === org.id ? 'opacity-100' : 'opacity-0'
                                    )}
                                />
                            </DropdownMenuItem>
                        ))
                    ) : (
                        <div className="p-3 text-center text-sm text-muted-foreground">
                            No organizations found
                        </div>
                    )}
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
